import React, { useContext, useEffect } from 'react'
import { Link } from 'react-router-dom'
import useCalculatePaymentMethodCardTotalPrice from '../../../../hooks/carts/useCalculatePaymentMethodCardTotalPrice'
import useCheckProvidersMinimumPrice from '../../../../hooks/carts/useCheckProvidersMinimumPrice'
import { PaymentMethodCartData } from '../../../../interfaces/carts'
import { handlePaymentMethodName } from '../../../../utils/functions/handlePaymentMethodName'
import { formatCurrency } from '../../../../utils/intl'
import ProviderCard from '../provider_card'
import CartAlert from '../cart_alert'
import AppContext from '../../../../context/AppContext'
import './index.scss'

interface Props {
  paymentMethodData: PaymentMethodCartData
}

function PaymentMethodCard({ paymentMethodData }: Props) {
  const { paymentMethod, providers } = paymentMethodData
  const { totalPrice } = useCalculatePaymentMethodCardTotalPrice(paymentMethodData)
  const { disabledButton, providersWithIssues } = useCheckProvidersMinimumPrice(paymentMethodData)
  const { stockAdjusted, setStockAdjusted } = useContext(AppContext)

  const hasMinimumIssue = providersWithIssues.length > 0
  const hasStockIssue = stockAdjusted.adjusted && stockAdjusted.reason === 'stock'

  // Limpiar alerta de stock después de mostrarla
  useEffect(() => {
    return () => {
      if (stockAdjusted.adjusted) {
        setStockAdjusted({
          adjusted: false,
          reason: null,
          productName: '',
          originalQuantity: 0,
          newQuantity: 0,
        })
      }
    }
  }, [])

  const getStockAdjustmentMessage = () => {
    if (!hasStockIssue) return ''
    
    const { productName, originalQuantity, newQuantity } = stockAdjusted
    
    if (newQuantity === 0) {
      return `El producto "${productName}" se agotó y fue removido de tu carrito.`
    }
    
    return `La cantidad de "${productName}" se ajustó de ${originalQuantity} a ${newQuantity} unidades por disponibilidad de stock.`
  }

  const getAlertReason = (): 'minimum' | 'stock' | 'both' | null => {
    if (hasMinimumIssue && hasStockIssue) return 'both'
    if (hasMinimumIssue) return 'minimum'
    if (hasStockIssue) return 'stock'
    return null
  }

  const alertReason = getAlertReason()

  // Debug logs
  console.log('🔍 Cart Alert Debug:', {
    alertReason,
    disabledButton,
    hasMinimumIssue,
    hasStockIssue,
    providersWithIssues,
    stockAdjusted
  })

  return (
    <div className="payment-method-card">
      <h4>{handlePaymentMethodName(paymentMethod)}</h4>

      {/* Alerta cuando hay problemas */}
      {alertReason && (
        <CartAlert
          reason={alertReason}
          providersWithMinimumIssue={providersWithIssues}
          stockAdjustmentMessage={getStockAdjustmentMessage()}
        />
      )}

      {/* Content para las tarjetas por proveedor */}
      <ul className="payment-method-card__providers">
        {providers.map((provider) => (
          <ProviderCard key={provider._id} provider={provider} paymentMethodData={paymentMethodData} />
        ))}
      </ul>

      <div
        className={`payment-method-card__button payment-method-card__button--${disabledButton ? 'disabled' : 'active'}`}
      >
        <Link
          to={disabledButton ? '#' : `/suggested/${paymentMethodData.paymentMethod}`}
          className={`payment-method-card__button__link payment-method-card__button payment-method-card__button__link--${
            disabledButton ? 'disabled' : 'active'
          }`}
        >
          {paymentMethod === 'pre-pago' ? 'Pagar' : 'Pedir'} Total: {formatCurrency(totalPrice)}
        </Link>
      </div>
    </div>
  )
}

export default PaymentMethodCard
